import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Wallet, Check, Loader2, Mic, Sparkles, ShieldCheck, Infinity as InfinityIcon, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  isPlayBillingAvailable,
  buyLifetimeAccess,
  findExistingLifetimePurchase,
  PRODUCT_ID,
} from "../lib/playBilling";

export default function PaywallPage() {
  const { api, refreshUser } = useAuth();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [loadingLicense, setLoadingLicense] = useState(true);
  const [playAvailable, setPlayAvailable] = useState(false);
  const didAutoRestore = useRef(false);
  const navigate = useNavigate();

  const sendTokenToBackend = useCallback(async (purchaseToken, productId, orderId = null) => {
    const r = await api.post("/android/play/verify-purchase", {
      purchase_token: purchaseToken,
      product_id: productId,
      order_id: orderId,
    });
    return r.data;
  }, [api]);

  const attemptRestore = useCallback(async ({ silent = false } = {}) => {
    if (!playAvailable) return false;
    try {
      const existing = await findExistingLifetimePurchase();
      if (!existing) {
        if (!silent) toast.info("Nenhuma compra encontrada neste Google Play.");
        return false;
      }
      console.log("[Paywall] Found existing Play purchase, restoring…");
      const data = await sendTokenToBackend(existing.purchaseToken, existing.productId);
      if (data.status === "granted" || data.status === "already_active") {
        toast.success("Compra restaurada! Acesso vitalício liberado.");
        await refreshUser();
        navigate("/", { replace: true });
        return true;
      }
      if (data.status === "pending") {
        if (!silent) toast.info("Pagamento pendente no Google Play. Liberaremos assim que confirmado.");
        return false;
      }
      return false;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "";
      console.warn("[Paywall] Restore failed:", msg);
      if (!silent) toast.error(msg || "Não foi possível restaurar a compra.");
      return false;
    }
  }, [playAvailable, sendTokenToBackend, refreshUser, navigate]);

  useEffect(() => {
    api.get("/license/me").then((r) => setLicense(r.data)).finally(() => setLoadingLicense(false));
    isPlayBillingAvailable().then(setPlayAvailable).catch(() => setPlayAvailable(false));
  }, [api]);

  useEffect(() => {
    if (license && !license.needs_payment && license.is_active) {
      navigate("/", { replace: true });
    }
  }, [license, navigate]);

  // Auto-restore once, when we know the TWA has Play Billing and the user still needs payment.
  useEffect(() => {
    if (didAutoRestore.current) return;
    if (!playAvailable) return;
    if (loadingLicense) return;
    if (!license || !license.needs_payment) return;
    didAutoRestore.current = true;
    (async () => {
      setRestoring(true);
      try {
        await attemptRestore({ silent: true });
      } finally {
        setRestoring(false);
      }
    })();
  }, [playAvailable, loadingLicense, license, attemptRestore]);

  const startStripeCheckout = async () => {
    setLoading(true);
    try {
      const r = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao iniciar pagamento");
      setLoading(false);
    }
  };

  const startPlayCheckout = async () => {
    setLoading(true);
    try {
      // 1) Last-chance restore before launching a new purchase — avoids ITEM_ALREADY_OWNED.
      const restored = await attemptRestore({ silent: true });
      if (restored) return; // navigated away already

      // 2) Real new purchase
      let purchaseResult;
      try {
        purchaseResult = await buyLifetimeAccess();
      } catch (err) {
        const msg = err?.message || "";
        console.warn("[Paywall] PaymentRequest failed:", msg);
        // Chrome sometimes maps ITEM_ALREADY_OWNED → clientAppUnavailable. Reconcile.
        const reconciled = await attemptRestore({ silent: true });
        if (reconciled) return;
        if (/cancel|abort|user/i.test(msg)) {
          setLoading(false);
          return;
        }
        toast.error(
          /clientAppUnavailable/i.test(msg)
            ? "O Google Play não conseguiu abrir a compra neste dispositivo. Tente novamente em alguns minutos."
            : (msg || "Erro ao processar a compra na Google Play.")
        );
        setLoading(false);
        return;
      }

      const { purchaseToken, productId, orderId } = purchaseResult;
      const data = await sendTokenToBackend(purchaseToken, productId, orderId || null);
      if (data.status === "granted" || data.status === "already_active") {
        toast.success("Acesso vitalício liberado!");
        await refreshUser();
        navigate("/", { replace: true });
      } else if (data.status === "pending") {
        toast.info("Pagamento pendente. Liberaremos assim que confirmado.");
        setLoading(false);
      } else {
        toast.error("Compra não liberada. Tente novamente.");
        setLoading(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "";
      if (!/cancel|abort|user/i.test(msg)) {
        toast.error(msg || "Erro ao processar a compra na Google Play.");
      }
      setLoading(false);
    }
  };

  const handleManualRestore = async () => {
    setRestoring(true);
    try {
      const ok = await attemptRestore({ silent: false });
      if (!ok) {
        // attemptRestore already surfaced info/error toasts.
      }
    } finally {
      setRestoring(false);
    }
  };

  const startCheckout = playAvailable ? startPlayCheckout : startStripeCheckout;
  const promoEnd = license?.promo_end ? new Date(license.promo_end).toLocaleDateString("pt-BR") : null;

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-lg animate-slide-up">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
            <Wallet className="w-6 h-6 text-slate-900" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-display text-xl font-bold text-white">Põe na Conta</p>
            <p className="text-xs text-emerald-300">Finanças por voz</p>
          </div>
        </div>

        <div className="pnc-card p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-24 -right-16 w-56 h-56 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center justify-center gap-1.5 mb-3">
              <Lock className="w-3.5 h-3.5 text-emerald-300" />
              <p className="text-[10px] uppercase tracking-[0.24em] text-emerald-300 font-semibold">Acesso vitalício</p>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-white text-center leading-tight">
              Desbloqueie o <span className="bg-gradient-to-r from-emerald-300 to-sky-300 bg-clip-text text-transparent">Põe na Conta</span>
            </h1>
            <p className="text-sm text-slate-400 text-center mt-3 max-w-sm mx-auto">
              Pagamento único. Sem mensalidade. Sem renovação. Seus dados ficam seus.
            </p>

            <div className="my-7 flex items-baseline justify-center gap-2">
              <span className="text-slate-400 text-sm">R$</span>
              <span className="font-display text-6xl font-black text-white font-mono-nums">
                {loadingLicense ? "—" : ((license?.price_brl_cents || 990) / 100).toFixed(2).replace(".", ",")}
              </span>
              <span className="text-slate-400 text-sm">à vista</span>
            </div>

            {promoEnd && license?.promo_active && (
              <p data-testid="promo-info" className="text-xs text-amber-300 text-center bg-amber-500/10 border border-amber-500/20 rounded-xl py-2 px-3 mb-5">
                🎉 Oferta de lançamento válida até <strong>{promoEnd}</strong>
              </p>
            )}

            <ul className="space-y-2.5 mb-7">
              {[
                { icon: Mic, text: "Registre despesas e receitas falando" },
                { icon: Sparkles, text: "IA categoriza e organiza em segundos" },
                { icon: InfinityIcon, text: "Acesso sem prazo de expiração" },
                { icon: ShieldCheck, text: "Seus dados criptografados e isolados" },
              ].map((f) => (
                <li key={f.text} className="flex items-center gap-3 text-sm text-slate-200">
                  <span className="w-7 h-7 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0">
                    <f.icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                  </span>
                  <span>{f.text}</span>
                </li>
              ))}
            </ul>

            <button
              data-testid="paywall-checkout-btn"
              onClick={startCheckout}
              disabled={loading || loadingLicense || restoring}
              className="btn-emerald w-full py-4 rounded-2xl flex items-center justify-center gap-2 text-base disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              {loading
                ? "Preparando pagamento…"
                : playAvailable
                ? "Comprar via Google Play"
                : "Comprar acesso vitalício"}
            </button>

            {playAvailable && (
              <button
                data-testid="paywall-restore-btn"
                onClick={handleManualRestore}
                disabled={loading || restoring || loadingLicense}
                className="w-full mt-3 py-3 rounded-2xl border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10 flex items-center justify-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {restoring ? "Verificando compra anterior…" : "Já comprei — restaurar"}
              </button>
            )}

            <p className="text-[11px] text-slate-500 text-center mt-4">
              {playAvailable
                ? "Pagamento processado com segurança pelo Google Play."
                : "Pagamento processado com segurança pela Stripe. Cartão, Pix e boleto (quando habilitados no painel do proprietário)."}
            </p>
          </div>
        </div>

        <div className="mt-5 text-center">
          <button
            onClick={() => navigate("/perfil")}
            data-testid="paywall-later-btn"
            className="text-xs text-slate-400 hover:text-white underline underline-offset-4"
          >
            Depois — voltar ao perfil
          </button>
        </div>
      </div>
    </div>
  );
}
